import React, { useState } from 'react';
import { Modal } from '@/components/molecules';
import { Button, Input } from '@/components/atoms';
import { useUpdatePurchaseOrderStatusMutation } from '@/hooks/useErpApi';
import { useDispatch } from 'react-redux';
import { updatePurchaseOrder } from '@/features/purchaseOrders';
import type { PurchaseOrder } from '@/schemas/purchaseOrders';

interface DeliveryDateModalProps {
  purchaseOrder: PurchaseOrder;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DeliveryDateModal: React.FC<DeliveryDateModalProps> = ({
  purchaseOrder,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const { mutate: updateStatus, isLoading } = useUpdatePurchaseOrderStatusMutation();
  const [actualDeliveryDate, setActualDeliveryDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!actualDeliveryDate) {
      setError('Actual delivery date is required');
      return;
    }

    try {
      const result = await updateStatus({
        id: purchaseOrder.id,
        data: {
          status: 'delivered',
          actual_delivery_date: new Date(actualDeliveryDate).toISOString(),
        },
      });
      dispatch(updatePurchaseOrder(result as never));
      setActualDeliveryDate('');
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update purchase order status to delivered');
    }
  };

  const handleCancel = () => {
    setActualDeliveryDate('');
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Mark Order as Delivered"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-3">
            <p className="text-error-700 text-sm">{error}</p>
          </div>
        )}

        <div>
          <p className="text-sm text-neutral-600 mb-4">
            Please provide the actual delivery date to mark this purchase order as delivered.
          </p>
        </div>

        <div>
          <label htmlFor="actual_delivery_date" className="block text-sm font-medium text-neutral-700 mb-2">
            Actual Delivery Date *
          </label>
          <Input
            id="actual_delivery_date"
            type="date"
            value={actualDeliveryDate}
            onChange={(e) => setActualDeliveryDate(e.target.value)}
            required
            className="w-full"
            max={new Date().toISOString().split('T')[0]} // Cannot be in the future
          />
          <p className="text-xs text-neutral-500 mt-1">
            The date when the goods were actually received
          </p>
        </div>

        <div className="flex gap-2 justify-end pt-4 border-t border-neutral-200">
          <Button type="button" variant="secondary" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading || !actualDeliveryDate}>
            {isLoading ? 'Updating...' : 'Mark as Delivered'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};




